import { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";

export interface MultiSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number;
  className?: string;
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select…",
  disabled = false,
  maxItems,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter(
    (o) =>
      !value.includes(o.value) &&
      o.label.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, []);

  function add(opt: MultiSelectOption) {
    if (opt.disabled) return;
    if (maxItems && value.length >= maxItems) return;
    onChange?.([...value, opt.value]);
    setQuery("");
    inputRef.current?.focus();
  }

  function remove(v: string) {
    onChange?.(value.filter((x) => x !== v));
  }

  const selectedOptions = value
    .map((v) => options.find((o) => o.value === v))
    .filter(Boolean) as MultiSelectOption[];

  const atMax = maxItems != null && value.length >= maxItems;

  return (
    <div ref={containerRef} className={cn("ms-root", open && "ms-open", disabled && "ms-disabled", className)}>
      <div
        className="ms-control"
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <div className="ms-values">
          {selectedOptions.map((opt) => (
            <span key={opt.value} className="ms-chip">
              {opt.label}
              <button
                className="ms-chip-remove"
                type="button"
                onMouseDown={(e) => { e.stopPropagation(); remove(opt.value); }}
                aria-label={`Remove ${opt.label}`}
              >
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
          {!atMax && (
            <input
              ref={inputRef}
              className="ms-input"
              placeholder={value.length === 0 ? placeholder : "Add more…"}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              disabled={disabled}
            />
          )}
        </div>
        <svg
          className={cn("ms-chevron", open && "ms-chevron-open")}
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="ms-dropdown">
          {filtered.length === 0 ? (
            <p className="ms-empty">{query ? "No matches" : atMax ? "Maximum reached" : "All options selected"}</p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                className={cn("ms-option", opt.disabled && "ms-option-disabled")}
                onMouseDown={(e) => { e.preventDefault(); add(opt); }}
                disabled={opt.disabled}
                type="button"
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
